// @flow

// 컴포넌트 하나의 오류로 앱 전체가 하얀 화면이 되지 않게 막는다 (#203).
// error boundary가 없으면 React는 렌더·effect 오류 하나에 루트까지 언마운트한다.
// 실제로 오프라인 중 조회 실패 → macroArray.sort 오류 하나로 번역자 화면 전체가 죽었다 (#201 테스트 중).
//
// 주의: 이벤트 핸들러·비동기 콜백(promise)의 오류는 boundary가 잡지 않는다. 그건 각자 처리해야 한다.

import * as Sentry from "@sentry/react";
import React, { useEffect } from "react";
import Card from "react-bootstrap/Card";
import Button from "react-bootstrap/Button";

type Props = {
  children: React$Node,
};

const tagBoundary = (name: string) => (scope: any) => {
  scope.setTag("errorBoundary", name);
};

function reloadPage() {
  window.location.reload();
}

// ── 화면 전체 ──────────────────────────────────────────────

function AppCrashFallback(): React$Element<any> {
  return (
    <div className="p-4">
      <Card border="danger">
        <Card.Body>
          <Card.Title>화면에 오류가 발생했습니다</Card.Title>
          <Card.Text>
            새로고침하면 이어서 작업할 수 있습니다. 보낸 자막은 서버에 저장되어 있습니다.
          </Card.Text>
          <Button variant="danger" onClick={reloadPage}>
            새로고침
          </Button>
        </Card.Body>
      </Card>
    </div>
  );
}

// 마지막 방어선: 아래 boundary가 없는 곳에서 터져도 하얀 화면 대신 안내를 띄운다
export function AppErrorBoundary(props: Props): React$Element<any> {
  return (
    <Sentry.ErrorBoundary
      fallback={<AppCrashFallback />}
      beforeCapture={tagBoundary("app")}
    >
      {props.children}
    </Sentry.ErrorBoundary>
  );
}

// ── 패널 ──────────────────────────────────────────────────

// 한 패널이 죽어도 나머지(특히 Input)는 살아 있게 한다. "다시 시도"는 그 패널만 다시 그린다
export function PanelErrorBoundary(
  props: Props & { name: string }
): React$Element<any> {
  return (
    <Sentry.ErrorBoundary
      fallback={({ resetError }) => (
        <Card border="danger">
          <Card.Body className="p-3">
            <Card.Text className="mb-2">
              {props.name} 패널에 오류가 발생했습니다. 다른 패널은 그대로 쓸 수 있습니다.
            </Card.Text>
            <Button
              size="sm"
              variant="outline-danger"
              className="me-2"
              onClick={resetError}
            >
              다시 시도
            </Button>
            <Button size="sm" variant="outline-secondary" onClick={reloadPage}>
              새로고침
            </Button>
          </Card.Body>
        </Card>
      )}
      beforeCapture={tagBoundary(`panel:${props.name}`)}
    >
      {props.children}
    </Sentry.ErrorBoundary>
  );
}

// ── 송출 화면 ──────────────────────────────────────────────

const BROADCAST_RELOADS_KEY = "broadcastCrashReloads";
const RELOAD_WINDOW_MS = 60 * 1000;
const MAX_QUICK_RELOADS = 3;
const QUICK_RELOAD_DELAY_MS = 3 * 1000;
const SLOW_RELOAD_DELAY_MS = 60 * 1000;

// 1분 안에 3번까지는 3초 뒤 새로고침, 그 이상 계속 죽으면 1분 간격으로만 시도한다 (무한 새로고침 방지)
export function planBroadcastReload(
  previous: Array<number>,
  now: number
): { delayMs: number, history: Array<number> } {
  const recent = previous.filter((t) => now - t < RELOAD_WINDOW_MS);
  const delayMs =
    recent.length < MAX_QUICK_RELOADS ? QUICK_RELOAD_DELAY_MS : SLOW_RELOAD_DELAY_MS;
  return { delayMs, history: [...recent, now] };
}

function readReloadHistory(): Array<number> {
  try {
    const parsed = JSON.parse(
      window.sessionStorage.getItem(BROADCAST_RELOADS_KEY) ?? "[]"
    );
    return Array.isArray(parsed) ? parsed.filter((t) => typeof t === "number") : [];
  } catch (e) {
    return [];
  }
}

function writeReloadHistory(history: Array<number>) {
  try {
    window.sessionStorage.setItem(BROADCAST_RELOADS_KEY, JSON.stringify(history));
  } catch (e) {
    // 저장이 안 되면 횟수 제한 없이 3초 뒤 새로고침 — 그래도 하얀 화면보다 낫다
  }
}

function BroadcastCrashFallback(): null {
  useEffect(() => {
    const { delayMs, history } = planBroadcastReload(
      readReloadHistory(),
      Date.now()
    );
    writeReloadHistory(history);
    const timer = setTimeout(reloadPage, delayMs);
    return () => clearTimeout(timer);
  }, []);
  // 방송에 오류 문구가 나가면 안 되므로 아무것도 그리지 않는다
  return null;
}

export function BroadcastErrorBoundary(props: Props): React$Element<any> {
  return (
    <Sentry.ErrorBoundary
      fallback={<BroadcastCrashFallback />}
      beforeCapture={tagBoundary("broadcast")}
    >
      {props.children}
    </Sentry.ErrorBoundary>
  );
}
